import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./AffiliateScreen.module.css";
import {
  FaCopy,
  FaTimes,
  FaUser,
  FaClock,
  FaTag,
  FaMoneyBillWave,
  FaChartLine,
  FaLink,
  FaInfoCircle,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaBullhorn,
} from "react-icons/fa";
import {
  generateAffiliateLink,
  getAffiliateStats,
  getAffiliateReferredTransactions,
} from "../utils/affiliateUtils";
import AffiliatePayoutManager from "./AffiliatePayoutManager";

const AffiliateScreen = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [affiliateLink, setAffiliateLink] = useState("");
  const [stats, setStats] = useState({
    totalClicks: 0,
    pendingSignups: 0,
    completedSignups: 0,
    pendingPayments: 0,
    completedPayments: 0,
    totalPayments: 0,
    totalCommission: 0,
    pendingCommission: 0,
    payments: [],
    signups: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats"); // "stats", "transactions", "payout"
  const [copiedLink, setCopiedLink] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [affiliatePercent, setAffiliatePercent] = useState(null); // Không gán cứng mặc định ban đầu để tránh chớp số
  const [campaign, setCampaign] = useState("");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [customLink, setCustomLink] = useState("");
  const [copiedCustomLink, setCopiedCustomLink] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [policyBannerDismissed, setPolicyBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem("affiliatePolicyBannerDismissed") === "true";
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const kpiMetrics = useMemo(() => {
    const paidTransactions = transactions.filter(
      (transaction) => transaction.status?.toUpperCase() === "PAID",
    );
    const pendingTransactions = transactions.filter((transaction) =>
      ["PENDING", "PROCESSING"].includes(transaction.status?.toUpperCase()),
    );
    const totalClicks = stats.totalClicks || 0;
    const conversionRate =
      totalClicks > 0
        ? ((paidTransactions.length / totalClicks) * 100).toFixed(1)
        : "0.0";
    const avgOrderValue =
      paidTransactions.length > 0
        ? Math.round(
            paidTransactions.reduce(
              (acc, transaction) => acc + (transaction.amount || 0),
              0,
            ) / paidTransactions.length,
          )
        : 0;

    return {
      paidTransactions,
      pendingTransactions,
      conversionRate,
      avgOrderValue,
    };
  }, [stats.totalClicks, transactions]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const normalizedStatus = transaction.status?.toUpperCase();
      const normalizedType = transaction.type === "plan" ? "PLAN" : "POINT";
      const matchStatus =
        statusFilter === "ALL" || normalizedStatus === statusFilter;
      const matchType = typeFilter === "ALL" || normalizedType === typeFilter;
      return matchStatus && matchType;
    });
  }, [transactions, statusFilter, typeFilter]);

  const checkLoginStatus = () => {
    const accessToken = localStorage.getItem("userAccessToken");
    return !!accessToken;
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const link = generateAffiliateLink(userId);
      setAffiliateLink(link);
      loadAffiliateData(userId);
    } else {
      setAffiliatePercent(30);
      setLoading(false);
    }
  }, []);

  const loadAffiliateData = async (userId) => {
    setLoading(true);
    try {
      console.log("🔄 Đang tải dữ liệu affiliate cho userId:", userId);

      // Sử dụng API mới để lấy thống kê
      const statsData = await getAffiliateStats(userId);
      console.log("📊 Dữ liệu thống kê affiliate:");
      console.log(statsData);

      if (statsData) {
        // Đảm bảo tất cả các thuộc tính đều tồn tại
        const processedData = {
          ...statsData,
          totalEarnings: statsData.stats?.totalEarnings || 0,
          totalSales: statsData.stats?.totalSales || 0,
          totalClicks: statsData.stats?.totalClicks || statsData.totalClicks || 0,
          completedSignups: statsData.stats?.completedSignups || 0,
          pendingSignups: statsData.stats?.pendingSignups || 0,
          signups: statsData.signups || {},
        };

        setStats(processedData);
        setAffiliatePercent(statsData.affiliatePercent ?? 30);
      } else {
        // Đặt giá trị mặc định nếu không có dữ liệu
        setStats({
          totalEarnings: 0,
          totalSales: 0,
          totalClicks: 0,
          pendingSignups: 0,
          completedSignups: 0,
          pendingPayments: 0,
          completedPayments: 0,
          totalPayments: 0,
          totalCommission: 0,
          pendingCommission: 0,
          payments: [],
          signups: {},
        });
      }

      // Lấy dữ liệu giao dịch từ API mới
      try {
        const transactionData = await getAffiliateReferredTransactions(userId);
        console.log(
          "📊 Dữ liệu giao dịch người dùng được giới thiệu:",
          transactionData,
        );
        setTransactions(transactionData.transactions || []);
      } catch (error) {
        console.error("Lỗi khi lấy dữ liệu giao dịch:", error);
        setTransactions([]);
      }
    } catch (error) {
      console.error("Lỗi khi lấy dữ liệu affiliate:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(affiliateLink).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    });
  };

  const handleJoinAffiliate = (plan) => {
    if (!checkLoginStatus()) {
      navigate("/login");
      return;
    }
    setShowPopup(true);
    setIsClosing(false);
  };

  const closePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowPopup(false);
      setIsClosing(false);
    }, 300);
  };

  const handleConfirm = () => {
    if (window.confirm("Bạn xác nhận muốn tham gia chương trình affiliate?")) {
      const userId = localStorage.getItem("userId");
      const currentTime = new Date().toLocaleString("vi-VN");

      const telegramApiUrl =
        process.env.REACT_APP_TELEGRAM_API_URL || "";
      const chatId =
        process.env.REACT_APP_TELEGRAM_CHAT_ID || "";

      if (!telegramApiUrl || !chatId) {
        console.warn(
          "Chưa cấu hình REACT_APP_TELEGRAM_API_URL hoặc REACT_APP_TELEGRAM_CHAT_ID",
        );
        closePopup();
        return;
      }

      const message = `
- User ID: ${userId}
- Đăng ký: Chương trình Affiliate
- Thời gian: ${currentTime}`;

      fetch(telegramApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Đã gửi thông báo đến Telegram:", data);
          closePopup();
        })
        .catch((error) => {
          console.error("Lỗi khi gửi thông báo:", error);
          closePopup();
        });
    }
  };

  const handleDismissPolicyBanner = () => {
    setPolicyBannerDismissed(true);
    try {
      localStorage.setItem("affiliatePolicyBannerDismissed", "true");
    } catch (error) {
      console.error("Lỗi khi lưu trạng thái banner:", error);
    }
  };

  const handlePopupClick = (event) => {
    if (event.target === event.currentTarget) {
      closePopup();
    }
  };

  const handleBack = () => {
    if (location.state?.fromMenu) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleContactClick = () => {
    window.open("/#contact", "_blank");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return "";
    }

    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${hours}:${minutes} - ${day}/${month}/${year}`;
  };

  const getTransactionCommission = (transaction) =>
    transaction?.affiliateInfo?.commission || 0;

  const getTransactionCommissionPercent = (transaction) =>
    transaction?.affiliateInfo?.percent ?? affiliatePercent ?? 30;

  const getStatusText = (status) => {
    switch (status) {
      case "PENDING":
        return "Chưa thanh toán";
      case "PAID":
        return "Đã thanh toán";
      case "CANCELED":
        return "Đã hủy";
      case "PROCESSING":
        return "Đang xử lý";
      default:
        return "Không xác định";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "PENDING":
        return styles.statusPending;
      case "PAID":
        return styles.statusCompleted;
      case "CANCELED":
        return styles.statusReview;
      case "PROCESSING":
        return styles.statusPending;
      default:
        return "";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PENDING":
      case "PROCESSING":
        return <FaHourglassHalf />;
      case "PAID":
        return <FaCheckCircle />;
      case "CANCELED":
        return <FaTimesCircle />;
      default:
        return <FaClock />;
    }
  };

  const handleViewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const closeTransactionDetails = () => {
    setShowTransactionDetails(false);
    setTimeout(() => {
      setSelectedTransaction(null);
    }, 300);
  };

  const handleTransactionDetailsClick = (event) => {
    if (event.target === event.currentTarget) {
      closeTransactionDetails();
    }
  };

  const handleCopyCustomLink = () => {
    navigator.clipboard.writeText(customLink).then(() => {
      setCopiedCustomLink(true);
      setTimeout(() => setCopiedCustomLink(false), 1500);
    });
  };

  const openCampaignPopup = () => {
    setShowCampaignPopup(true);
  };

  const closeCampaignPopup = () => {
    setShowCampaignPopup(false);
    setTimeout(() => {
      setCampaign("");
      setSource("");
      setMedium("");
      setCustomLink("");
    }, 300);
  };

  const handleCampaignPopupClick = (event) => {
    if (event.target === event.currentTarget) {
      closeCampaignPopup();
    }
  };

  const generateCustomAffiliateLink = () => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      let link = `${window.location.origin}/?ref=${userId}`;
      if (campaign) link += `&utm_campaign=${encodeURIComponent(campaign)}`;
      if (source) link += `&utm_source=${encodeURIComponent(source)}`;
      if (medium) link += `&utm_medium=${encodeURIComponent(medium)}`;
      setCustomLink(link);
    }
  };

  const renderMetricLabel = (label, tooltip) => (
    <span className={styles.metricLabel}>
      {label}
      <span
        className={styles.tooltipIcon}
        tabIndex={0}
        role="button"
        aria-label={`Giải thích cho chỉ số ${label}`}
      >
        <FaInfoCircle />
        <span className={styles.tooltipText}>{tooltip}</span>
      </span>
    </span>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.heroCard}>
          <div>
            <p className={styles.eyebrow}>Affiliate Control Center</p>
            <h1 className={styles.title}>Theo dõi hoa hồng</h1>
            <p className={styles.slogan}>
              Tạo link giới thiệu, đo lường chuyển đổi và tối ưu doanh thu
              affiliate trong một màn hình duy nhất.
            </p>
          </div>
          {checkLoginStatus() && (
            <button
              onClick={handleCopyLink}
              className={`${styles.primaryButton} ${
                copiedLink ? styles.copied : ""
              }`}
            >
              <FaCopy className={styles.copyIcon} />
              {copiedLink ? "Đã sao chép link" : "Sao chép link giới thiệu"}
            </button>
          )}
        </div>

        {!policyBannerDismissed && (
          <div className={styles.policyBanner}>
            <div className={styles.policyBannerIcon}>
              <FaBullhorn />
            </div>
            <div className={styles.policyBannerContent}>
              <h3 className={styles.policyBannerTitle}>
                Chính sách chương trình Affiliate
              </h3>
              <ul className={styles.policyBannerList}>
                <li>
                  <strong>Hoa hồng {affiliatePercent !== null ? affiliatePercent : "..."}%</strong> trên mỗi giao
                  dịch từ người dùng bạn giới thiệu.
                </li>
                <li>
                  Áp dụng cho <strong>cả Mua Plan lẫn Mua Điểm</strong> — mọi
                  giao dịch đều được tính hoa hồng.
                </li>
                <li>
                  <strong>Thời hạn 1 năm</strong> — hoa hồng được tính trong
                  vòng 1 năm (12 tháng) kể từ khi người dùng đăng ký qua link của bạn.
                </li>
                <li>
                  <strong>Cookie 30 ngày</strong> — link giới thiệu có hiệu lực
                  30 ngày kể từ lần click cuối.
                </li>
                <li>
                  Hoa hồng được ghi nhận <strong>tự động</strong> khi giao dịch
                  được thanh toán thành công.
                </li>
                <li>
                  Mỗi tài khoản mới chỉ được quy về <strong>một</strong> người
                  giới thiệu từ lần click hợp lệ cuối trước đăng ký (last-click attribution).
                </li>
              </ul>
            </div>
            <button
              className={styles.policyBannerClose}
              onClick={handleDismissPolicyBanner}
              aria-label="Đóng thông báo chính sách"
            >
              <FaTimes />
            </button>
          </div>
        )}

        <div className={styles.howItWorks}>
          <h2>Quy trình 2 bước để bắt đầu kiếm tiền</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <h3>Chia sẻ</h3>
              <p>
                Quảng bá website thông qua link giới thiệu độc quyền của bạn
              </p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <h3>Kiếm tiền</h3>
              <p>
                Nhận hoa hồng <strong>{affiliatePercent !== null ? affiliatePercent : "..."}%</strong> khi
                người dùng mua Plan hoặc mua Điểm — cả 2 đều được tính, suốt 1
                năm (12 tháng)
              </p>
            </div>
          </div>
        </div>

        {checkLoginStatus() && (
          <div className={styles.affiliateStats}>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <p>
                  {renderMetricLabel(
                    "Tổng thu nhập",
                    "Tổng hoa hồng bạn đã nhận từ các giao dịch thành công.",
                  )}
                </p>
                <h3>{(stats.totalEarnings || 0).toLocaleString()}đ</h3>
              </div>
              <div className={styles.kpiCard}>
                <p>
                  {renderMetricLabel(
                    "Tổng doanh số",
                    "Tổng giá trị giao dịch phát sinh từ người dùng đi qua link giới thiệu của bạn.",
                  )}
                </p>
                <h3>{(stats.totalSales || 0).toLocaleString()}đ</h3>
              </div>
              <div className={styles.kpiCard}>
                <p>
                  {renderMetricLabel(
                    "Tỉ lệ chuyển đổi",
                    "Tỷ lệ giao dịch đã thanh toán trên tổng số lượt click vào link giới thiệu.",
                  )}
                </p>
                <h3>{kpiMetrics.conversionRate}%</h3>
              </div>
              <div className={styles.kpiCard}>
                <p>
                  {renderMetricLabel(
                    "Giá trị đơn TB",
                    "Giá trị trung bình của các giao dịch đã thanh toán từ nguồn affiliate.",
                  )}
                </p>
                <h3>{kpiMetrics.avgOrderValue.toLocaleString()}đ</h3>
              </div>
            </div>

            <div className={styles.quickStats}>
              <div className={styles.quickStatChip}>
                {renderMetricLabel(
                  `Paid: ${kpiMetrics.paidTransactions.length}`,
                  "Số giao dịch đã xác nhận thanh toán.",
                )}
              </div>
              <div className={styles.quickStatChip}>
                {renderMetricLabel(
                  `Pending: ${kpiMetrics.pendingTransactions.length}`,
                  "Số giao dịch đang chờ thanh toán hoặc đang xử lý.",
                )}
              </div>
              <div className={styles.quickStatChip}>
                {renderMetricLabel(
                  `Clicks: ${stats.totalClicks || 0}`,
                  "Tổng số lượt nhấp vào link giới thiệu của bạn.",
                )}
              </div>
              <div className={styles.quickStatChip}>
                {renderMetricLabel(
                  `Hoa hồng: ${affiliatePercent !== null ? affiliatePercent : "..."}%`,
                  "Phần trăm hoa hồng bạn nhận trên doanh số hợp lệ.",
                )}
              </div>
            </div>

            <div className={styles.affiliateLinkSection}>
              <h3>Link giới thiệu của bạn</h3>
              <div className={styles.linkContainer}>
                <input
                  type="text"
                  value={affiliateLink}
                  readOnly
                  className={styles.linkInput}
                />
                <button
                  onClick={handleCopyLink}
                  className={`${styles.copyButton} ${
                    copiedLink ? styles.copied : ""
                  }`}
                >
                  <FaCopy className={styles.copyIcon} />
                  {copiedLink ? "Đã sao chép!" : "Sao chép"}
                </button>
              </div>
              <p className={styles.linkHelp}>
                Chia sẻ link để nhận hoa hồng{" "}
                <strong>{affiliatePercent !== null ? affiliatePercent : "..."}% </strong>
                khi người dùng thanh toán plan hoặc mua điểm.
              </p>
            </div>

            <div className={styles.tabsContainer}>
              <button
                className={`${styles.tabButton} ${
                  activeTab === "stats" ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab("stats")}
              >
                Thống kê tổng quan
              </button>
              <button
                className={`${styles.tabButton} ${
                  activeTab === "transactions" ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab("transactions")}
              >
                Danh sách giao dịch
              </button>
              <button
                className={`${styles.tabButton} ${
                  activeTab === "payout" ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab("payout")}
              >
                Nhận tiền & Rút tiền
              </button>
            </div>

            {loading ? (
              <div className={styles.loading}>
                Đang tải dữ liệu affiliate...
              </div>
            ) : (
              <>
                {activeTab === "stats" && (
                  <>
                    <h2>Hiệu suất giới thiệu</h2>
                    <div className={styles.statsGrid}>
                      <div className={styles.statCard}>
                        <h3>
                          {renderMetricLabel(
                            "Khách hàng đã thanh toán",
                            "Số khách đã hoàn tất ít nhất một giao dịch thành công qua link giới thiệu.",
                          )}
                        </h3>
                        <p className={styles.statValue}>
                          {kpiMetrics.paidTransactions.length || 0}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <h3>
                          {renderMetricLabel(
                            "Tổng người đăng ký",
                            "Tổng số người dùng đã đăng ký từ nguồn giới thiệu của bạn.",
                          )}
                        </h3>
                        <p className={styles.statValue}>
                          {stats.signups ? Object.keys(stats.signups).length : 0}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <h3>
                          {renderMetricLabel(
                            "Tổng lượt click",
                            "Số lần người dùng nhấp vào link affiliate trong toàn bộ thời gian.",
                          )}
                        </h3>
                        <p className={styles.statValue}>
                          {stats.totalClicks || 0}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <h3>
                          {renderMetricLabel(
                            "Giao dịch chờ xử lý",
                            "Các giao dịch đang ở trạng thái chờ thanh toán hoặc đang xử lý.",
                          )}
                        </h3>
                        <p className={styles.statValue}>
                          {kpiMetrics.pendingTransactions.length || 0}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === "transactions" && (
                  <div className={styles.signupsContainer}>
                    <h2>Danh sách giao dịch</h2>
                    <div className={styles.filtersRow}>
                      <select
                        value={statusFilter}
                        onChange={(event) =>
                          setStatusFilter(event.target.value)
                        }
                        className={styles.filterSelect}
                      >
                        <option value="ALL">Tất cả trạng thái</option>
                        <option value="PAID">Đã thanh toán</option>
                        <option value="PENDING">Chưa thanh toán</option>
                        <option value="PROCESSING">Đang xử lý</option>
                        <option value="CANCELED">Đã hủy</option>
                      </select>
                      <select
                        value={typeFilter}
                        onChange={(event) => setTypeFilter(event.target.value)}
                        className={styles.filterSelect}
                      >
                        <option value="ALL">Tất cả loại</option>
                        <option value="PLAN">Gói dịch vụ</option>
                        <option value="POINT">Điểm</option>
                      </select>
                    </div>

                    {filteredTransactions.length > 0 ? (
                      <div className={styles.tableContainer}>
                        <table className={styles.signupsTable}>
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Thời gian</th>
                              <th>Trạng thái</th>
                              <th>Loại</th>
                              <th>Sản phẩm</th>
                              <th>Số tiền</th>
                              <th>Hoa hồng</th>
                              <th>Thao tác</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTransactions.map((transaction) => (
                              <tr key={transaction.id}>
                                <td>{transaction.id.substring(0, 8)}...</td>
                                <td>
                                  {transaction.time ||
                                    formatDate(
                                      transaction.createdAt ||
                                        transaction.timestamp,
                                    )}
                                </td>
                                <td
                                  className={getStatusClass(transaction.status)}
                                >
                                  <span className={styles.statusWithIcon}>
                                    {getStatusIcon(transaction.status)}
                                  </span>
                                  {getStatusText(transaction.status)}
                                </td>
                                <td>
                                  {transaction.type === "plan"
                                    ? "Gói dịch vụ"
                                    : "Điểm"}
                                </td>
                                <td>{transaction.itemName || "-"}</td>
                                <td>
                                  {transaction.amount
                                    ? `${transaction.amount.toLocaleString()}đ`
                                    : "-"}
                                </td>
                                <td>
                                  {transaction.affiliateInfo
                                    ? `${getTransactionCommission(
                                        transaction,
                                      ).toLocaleString()}đ (${getTransactionCommissionPercent(
                                        transaction,
                                      )}%)`
                                    : "-"}
                                </td>
                                <td>
                                  <button
                                    className={styles.viewDetailsButton}
                                    onClick={() =>
                                      handleViewTransactionDetails(transaction)
                                    }
                                  >
                                    Xem chi tiết
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        Không có giao dịch phù hợp với bộ lọc hiện tại.
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "campaigns" && (
                  <div className={styles.campaignsContainer}>
                    <h2>Chiến dịch tiếp thị</h2>
                    <p className={styles.campaignsDescription}>
                      Tạo và quản lý các chiến dịch tiếp thị khác nhau để theo
                      dõi hiệu quả từng kênh.
                    </p>

                    <div className={styles.campaignCard}>
                      <div className={styles.campaignHeader}>
                        <h3>Tạo chiến dịch mới</h3>
                        <FaLink className={styles.campaignIcon} />
                      </div>
                      <p>
                        Tạo link giới thiệu tùy chỉnh với các tham số UTM để
                        theo dõi hiệu quả từng chiến dịch marketing.
                      </p>
                      <button
                        className={styles.createCampaignButton}
                        onClick={openCampaignPopup}
                      >
                        Tạo chiến dịch mới
                      </button>
                    </div>

                    <div className={styles.campaignTips}>
                      <h3>Mẹo sử dụng hiệu quả</h3>
                      <ul>
                        <li>
                          Tạo link riêng cho mỗi kênh marketing (Facebook,
                          Instagram, Email...)
                        </li>
                        <li>
                          Đặt tên chiến dịch dễ nhớ và liên quan đến nội dung
                        </li>
                        <li>
                          Theo dõi hiệu quả của từng chiến dịch trong tab "Danh
                          sách giao dịch"
                        </li>
                        <li>
                          Tập trung vào các chiến dịch mang lại nhiều chuyển đổi
                          nhất
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {activeTab === "payout" && (
                  <AffiliatePayoutManager userId={localStorage.getItem("userId")} />
                )}
              </>
            )}
          </div>
        )}

        {/* <div className={styles.plansContainer}>
          {affiliatePlans.map((plan) => (
            <div
              key={plan.name}
              className={`${styles.planCard} ${
                plan.recommended ? styles.recommended : ""
              }`}
            >
              {plan.recommended && (
                <div className={styles.recommendedBadge}>Phổ biến nhất</div>
              )}
              <h2 className={styles.planName}>{plan.name}</h2>
              <div className={styles.price}>
                <span className={styles.amount}>{plan.commission}</span>
                <span className={styles.period}> hoa hồng</span>
              </div>
              {!plan.isCustom && (
                <div className={styles.minPayout}>
                  Rút tiền tối thiểu: {plan.minPayout}đ
                </div>
              )}
              <ul className={styles.features}>
                {plan.features.map((feature, index) => (
                  <li key={index} className={styles.feature}>
                    <span className={styles.checkmark}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className={`${styles.button} ${
                  plan.recommended ? styles.recommendedButton : ""
                }`}
                onClick={() => {
                  if (plan.isCustom) {
                    handleContactClick();
                  } else {
                    handleJoinAffiliate(plan);
                  }
                }}
              >
                {plan.isCustom ? "Liên hệ ngay" : "Tham gia ngay"}
              </button>
            </div>
          ))}
        </div> */}

        <div className={styles.faq}>
          <h2>Câu hỏi thường gặp</h2>
          <div className={styles.faqList}>
            <div className={styles.faqItem}>
              <h3>Làm thế nào để bắt đầu?</h3>
              <p>
                Chỉ cần đăng ký tài khoản, chọn gói affiliate phù hợp và bắt đầu
                chia sẻ link giới thiệu của bạn.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Khi nào tôi nhận được tiền?</h3>
              <p>
                Hoa hồng được tính ngay khi có người dùng mới đăng ký qua link
                của bạn. Bạn có thể rút tiền khi đạt mức tối thiểu của gói đã
                chọn.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Tôi có thể kiếm được bao nhiêu?</h3>
              <p>
                Thu nhập của bạn không giới hạn, phụ thuộc vào số lượng người
                dùng mới bạn giới thiệu được.
              </p>
            </div>
          </div>
        </div>

        {showTransactionDetails && selectedTransaction && (
          <div
            className={`${styles.popup} ${isClosing ? styles.fadeOut : ""}`}
            onClick={handleTransactionDetailsClick}
          >
            <div
              className={`${styles.popupContent} ${styles.detailsPopup} ${
                isClosing ? styles.scaleOut : ""
              }`}
            >
              <div className={styles.detailsHeader}>
                <h3>Chi tiết giao dịch</h3>
                <button
                  className={styles.closeButton}
                  onClick={closeTransactionDetails}
                >
                  <FaTimes />
                </button>
              </div>

              <div className={styles.detailsContent}>
                <div className={styles.detailItem}>
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>ID giao dịch:</span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.id}
                    </span>
                  </div>
                </div>

                <div className={styles.detailItem}>
                  <FaUser className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>ID người dùng:</span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.userId}
                    </span>
                  </div>
                </div>

                <div className={styles.detailItem}>
                  <FaClock className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>
                      Thời gian giao dịch:
                    </span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.time ||
                        formatDate(
                          selectedTransaction.createdAt ||
                            selectedTransaction.timestamp,
                        )}
                    </span>
                  </div>
                </div>

                <div className={styles.detailItem}>
                  <FaTag className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>Trạng thái:</span>
                    <span
                      className={`${styles.detailValue} ${getStatusClass(
                        selectedTransaction.status,
                      )}`}
                    >
                      {getStatusText(selectedTransaction.status)}
                    </span>
                  </div>
                </div>

                <div className={styles.detailItem}>
                  <FaTag className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>Loại giao dịch:</span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.type === "plan"
                        ? "Gói dịch vụ"
                        : "Điểm"}
                    </span>
                  </div>
                </div>

                {selectedTransaction.itemName && (
                  <div className={styles.detailItem}>
                    <FaTag className={styles.detailIcon} />
                    <div className={styles.detailInfo}>
                      <span className={styles.detailLabel}>Sản phẩm:</span>
                      <span className={styles.detailValue}>
                        {selectedTransaction.itemName}
                      </span>
                    </div>
                  </div>
                )}

                <div className={styles.detailItem}>
                  <FaMoneyBillWave className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>
                      Số tiền thanh toán:
                    </span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.amount
                        ? `${selectedTransaction.amount.toLocaleString()}đ`
                        : "-"}
                    </span>
                  </div>
                </div>

                <div className={styles.detailItem}>
                  <FaMoneyBillWave className={styles.detailIcon} />
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>Hoa hồng:</span>
                    <span className={styles.detailValue}>
                      {selectedTransaction.affiliateInfo
                        ? `${getTransactionCommission(
                            selectedTransaction,
                          ).toLocaleString()}đ (${getTransactionCommissionPercent(
                            selectedTransaction,
                          )}%)`
                        : "-"}
                    </span>
                  </div>
                </div>

                {selectedTransaction.description && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailInfo}>
                      <span className={styles.detailLabel}>Mô tả:</span>
                      <span className={styles.detailValue}>
                        {selectedTransaction.description}
                      </span>
                    </div>
                  </div>
                )}

                {selectedTransaction.orderCode && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailInfo}>
                      <span className={styles.detailLabel}>Mã đơn hàng:</span>
                      <span className={styles.detailValue}>
                        {selectedTransaction.orderCode}
                      </span>
                    </div>
                  </div>
                )}

                {selectedTransaction.paymentInfo && (
                  <div className={styles.paymentInfoSection}>
                    <h4>Thông tin thanh toán</h4>
                    {selectedTransaction.paymentInfo.transactionDateTime && (
                      <div className={styles.detailItem}>
                        <div className={styles.detailInfo}>
                          <span className={styles.detailLabel}>
                            Thời gian thanh toán:
                          </span>
                          <span className={styles.detailValue}>
                            {
                              selectedTransaction.paymentInfo
                                .transactionDateTime
                            }
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedTransaction.paymentInfo.reference && (
                      <div className={styles.detailItem}>
                        <div className={styles.detailInfo}>
                          <span className={styles.detailLabel}>
                            Mã tham chiếu:
                          </span>
                          <span className={styles.detailValue}>
                            {selectedTransaction.paymentInfo.reference}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showCampaignPopup && (
          <div
            className={`${styles.popup} ${isClosing ? styles.fadeOut : ""}`}
            onClick={handleCampaignPopupClick}
          >
            <div
              className={`${styles.popupContent} ${styles.campaignPopup} ${
                isClosing ? styles.scaleOut : ""
              }`}
            >
              <div className={styles.detailsHeader}>
                <h3>Tạo link chiến dịch tùy chỉnh</h3>
                <button
                  className={styles.closeButton}
                  onClick={closeCampaignPopup}
                >
                  <FaTimes />
                </button>
              </div>

              <div className={styles.campaignForm}>
                <div className={styles.inputGroup}>
                  <label htmlFor="campaign">Tên chiến dịch:</label>
                  <input
                    type="text"
                    id="campaign"
                    placeholder="summer_sale, black_friday..."
                    value={campaign}
                    onChange={(e) => setCampaign(e.target.value)}
                    className={styles.campaignInput}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="source">Nguồn:</label>
                  <input
                    type="text"
                    id="source"
                    placeholder="facebook, instagram, email..."
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className={styles.campaignInput}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="medium">Phương tiện:</label>
                  <input
                    type="text"
                    id="medium"
                    placeholder="cpc, banner, email..."
                    value={medium}
                    onChange={(e) => setMedium(e.target.value)}
                    className={styles.campaignInput}
                  />
                </div>

                <button
                  className={styles.generateButton}
                  onClick={generateCustomAffiliateLink}
                  disabled={!campaign}
                >
                  Tạo link
                </button>

                {customLink && (
                  <div className={styles.customLinkResult}>
                    <p>Link chiến dịch của bạn:</p>
                    <div className={styles.linkContainer}>
                      <input
                        type="text"
                        value={customLink}
                        readOnly
                        className={styles.linkInput}
                      />
                      <button
                        onClick={handleCopyCustomLink}
                        className={`${styles.copyButton} ${
                          copiedCustomLink ? styles.copied : ""
                        }`}
                      >
                        <FaCopy className={styles.copyIcon} />
                        {copiedCustomLink ? "Đã sao chép!" : "Sao chép"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showPopup && (
          <div
            className={`${styles.popup} ${isClosing ? styles.fadeOut : ""}`}
            onClick={handlePopupClick}
          >
            <div
              className={`${styles.popupContent} ${
                isClosing ? styles.scaleOut : ""
              }`}
            >
              <h3>Đăng ký tham gia chương trình Affiliate</h3>
              <div className={styles.popupInfo}>
                <p>
                  Cảm ơn bạn đã quan tâm đến chương trình Affiliate của chúng
                  tôi. Vui lòng kiểm tra email của bạn trong vài phút tới để
                  nhận thông tin chi tiết về tài khoản và hướng dẫn bắt đầu.
                </p>
              </div>
              <button
                className={styles.confirmButton}
                onClick={handleConfirm}
                style={{ marginTop: "20px" }}
              >
                Xác nhận tham gia
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateScreen;
